function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

const PUB_SUB_EVENTS = {
  cartAdd: "cart-add",
  cartUpdate: "cart-update",
  selectOption: "select-option",
};

function hasActiveOverlays() {
  const hasActiveDrawer = document.querySelector(".drawer.active") !== null;
  const isQuickAddActive =
    document.body.classList.contains("quick-add-active") ||
    document.querySelector(".quick-add.quick-add--open") !== null;

  return hasActiveDrawer || isQuickAddActive;
}

function syncBodyOverlayState() {
  if (hasActiveOverlays()) {
    document.body.classList.add("blur");
  } else {
    document.body.classList.remove("blur");
    document.body.classList.remove("blur--body-only");
  }
}

window.addEventListener(
  "resize",
  debounce(() => {
    syncBodyOverlayState();
  }, 100),
);

//Drawer used for mobile navigation and mini cart
class SideDrawerToggle extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", function (evt) {
      evt.preventDefault();
      evt.stopPropagation();

      if (
        (this.getAttribute("data-drawer") == "mini-cart" &&
          document.body.classList.contains("drawer--navigation-active")) ||
        (this.getAttribute("data-drawer") == "mobile-menu" &&
          document.getElementById("mini-cart").classList.contains("active"))
      ) {
        this.closeAllDrawers();
      }

      if (
        this.getAttribute("data-drawer") == "mini-cart" &&
        document.body.classList.contains("megamenu--active")
      ) {
        document.body.classList.remove("megamenu--active");
      }

      if (
        this.getAttribute("data-drawer") == "mini-cart" &&
        !document.getElementById("mini-cart").classList.contains("active")
      ) {
        document
          .getElementById("mini-cart")
          .querySelector(".cart-drawer__content")
          .scrollTo(0, 0);
      }

      // Close all drawers
      if (this.getAttribute("data-drawer") == "all") {
        this.closeAllDrawers();
      } else {
        const drawer = document.getElementById(
          this.getAttribute("data-drawer"),
        );
        //Toggle drawer event
        const event = new Event("drawer-toggle");
        drawer.dispatchEvent(event);
      }
    });
  }

  closeAllDrawers() {
    const activeDrawers = document.querySelectorAll("side-drawer.active");
    if (activeDrawers) {
      activeDrawers.forEach((el) => {
        //Toggle drawer event
        const event = new Event("drawer-toggle");
        el.dispatchEvent(event);
      });
    }
  }
}

customElements.define("side-drawer-toggle", SideDrawerToggle);

class SideDrawer extends HTMLElement {
  connectedCallback() {
    if (
      this.classList.contains("size-guide-drawer") &&
      this.parentElement !== document.body
    ) {
      if (this.id) {
        const existing = document.body.querySelector(
          ":scope > #" + CSS.escape(this.id),
        );
        if (existing) existing.remove();
      }
      document.body.appendChild(this);
    }
  }

  constructor() {
    super();
    this._wasOpen = false;
    this._scrollPosition = 0;

    const thisElement = this;

    this.observer = new MutationObserver(() => {
      if (this._wasOpen && !this.hasAttribute("drawer-open")) {
        thisElement.open(this);
      }
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ["drawer-open"],
    });

    //Toggle event listeners
    this.addEventListener("drawer-toggle", function () {
      if (this.hasAttribute("drawer-open")) {
        thisElement.close(this);
      } else {
        thisElement.open(this);
      }
    });

    //Key up and body click events
    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
  }

  //Open drawer
  open(drawer) {
    this._wasOpen = true;
    this.setAttribute("drawer-open", "");
    this.classList.add("active");

    if (this.classList.contains("drawer--navigation")) {
      document.body.classList.add("drawer--navigation-active");
    }

    if (this.classList.contains("drawer--body-only")) {
      document.body.classList.add("blur--body-only");
    }
    // Prevent body scroll
    document.body.style.overflow = "hidden";

    document.body.classList.add("blur");
    document.body.classList.add("drawer--active");
    this.addEventListener(
      "transitionend",
      () => {
        drawer.focus();
        trapFocus(drawer);
      },
      { once: true },
    );

    document.body.addEventListener("click", this.onBodyClick, {
      passive: true,
    });

    // Prevent touchmove to avoid iOS scroll issues
    document.addEventListener("touchmove", this.onTouchMove, {
      passive: false,
    });
  }

  //Close drawer
  close() {
    this._wasOpen = false;
    this.removeAttribute("drawer-open");
    this.classList.remove("active");
    const activeDrawers = document.querySelectorAll(".drawer.active");
    if (activeDrawers.length == 0) {
      if (!hasActiveOverlays()) {
        document.body.classList.remove("blur");
        document.body.classList.remove("blur--body-only");
      }

      document.body.classList.remove("drawer--active");
      document.body.classList.remove("cart-items-added");

      // Restore body scroll
      document.body.style.overflow = "";

    }

    if (
      activeDrawers.length == 0 ||
      (activeDrawers.length == 1 &&
        !activeDrawers[0].classList.contains("drawer--navigation"))
    ) {
      document.body.classList.remove("drawer--navigation-active");
    }

    document.body.removeEventListener("click", this.onBodyClick, {
      passive: true,
    });

    document.removeEventListener("touchmove", this.onTouchMove, {
      passive: false,
    });

    removeTrapFocus(this);

    if (this.id === "mini-cart") {
      const cartTitle = this.querySelector(".title--default");
      if (cartTitle && cartTitle.classList.contains("hidden")) {
        cartTitle.classList.remove("hidden");
        cartTitle.nextElementSibling.classList.add("hidden");
      }
    }

    syncBodyOverlayState();
  }

  //Body click event to close drawer
  handleBodyClick(evt) {
    const rawTarget = evt.target;
    if (!rawTarget || !rawTarget.isConnected) return;

    const target =
      rawTarget.nodeType === Node.TEXT_NODE
        ? rawTarget.parentElement
        : rawTarget;
    if (!target) return;

    const drawerElement = target.closest("side-drawer");
    const isInsideAnyDrawer = drawerElement !== null;
    const isOnToggle = target.closest("side-drawer-toggle") !== null;

    if (!isInsideAnyDrawer && !isOnToggle) {
      this.close();
    }
  }

  //Prevent scrolling outside drawer on iOS
  handleTouchMove(evt) {
    if (!evt.target.closest("side-drawer")) {
      evt.preventDefault();
    }
  }
}

customElements.define("side-drawer", SideDrawer);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.cartItemsComponent = this.closest("cart-items");
    this.input = this.querySelector("input");
    this.key = this.input.getAttribute("data-item-key");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.onButtonClick(button);
      }),
    );
  }

  onButtonClick(button) {
    const previousValue = this.input.value;
    button.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      if (this.cartItemsComponent) {
        this.cartItemsComponent.classList.add("loading");
      }
      this.onChange();
      this.input.dispatchEvent(this.changeEvent);
    }
  }

  onChange() {
    updateCartItem(this.key, this.input.value);
  }
}

customElements.define("quantity-input", QuantityInput);

class Dropdown extends HTMLElement {
  constructor() {
    super();

    this.EXPANDED_CLASS = "dropdown--expanded";
    this.ACTIVE_OPTION_CLASS = "dropdown__option--active";

    this.isExpanded = false;
    this.boundHandleBodyClick = this.handleBodyClick.bind(this);
  }

  /**
   * Called when the element is added to the DOM.
   * Sets up event listeners.
   */
  connectedCallback() {
    this.addListeners();
  }

  /**
   * Adds event listeners to the trigger and options.
   */
  addListeners() {
    this.trigger.addEventListener("keydown", (event) =>
      this.handleTriggerKeyPress(event),
    );
    this.trigger.addEventListener("click", () => this.handleTriggerClick());
  }

  /**
   * Closes the dropdown if a click occurs outside of it.
   * @param event
   */
  handleBodyClick(event) {
    if (!this.contains(event.target) && this.isExpanded) {
      this.close();
    }
  }

  /**
   * Toggles the dropdown open/closed when the trigger is clicked.
   */
  handleTriggerClick() {
    this.isExpanded ? this.close() : this.open();
  }

  /**
   * Opens the dropdown.
   */
  open() {
    this.trigger.setAttribute("aria-expanded", "true");
    this.trigger.focus();
    this.classList.add(this.EXPANDED_CLASS);
    this.isExpanded = true;

    document.addEventListener("pointerdown", this.boundHandleBodyClick, true);
  }

  /**
   * Closes the dropdown.
   */
  close() {
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.focus();
    this.classList.remove(this.EXPANDED_CLASS);
    this.isExpanded = false;

    document.removeEventListener(
      "pointerdown",
      this.boundHandleBodyClick,
      true,
    );
  }

  /**
   * Handles key presses on the select trigger.
   * @param event
   */
  handleTriggerKeyPress(event) {
    event.preventDefault();

    switch (event.key) {
      case "Tab":
        this.close();
        break;

      case "Escape":
      case "Enter":
        this.isExpanded ? this.close() : this.open();
    }
  }

  /**
   * Returns the trigger button element.
   */
  get trigger() {
    return this.querySelector("[data-dropdown-trigger]");
  }

  /**
   * @returns {HTMLElement} The dropdown element
   */
  get dropdown() {
    return this.querySelector("[data-dropdown]");
  }
}

customElements.define("dropdown-wrapper", Dropdown);

/* Essential Preorder — campaign-based card CTA text (theme customization).
   Detects which preorder campaign a product-card belongs to and rewrites its
   CTA (.card-product__button span). Campaign membership is resolved from the
   card's product: embedded variants JSON first (no network), else a cached
   /products/{handle}.js fetch keyed off the card's own data attributes. */
(function () {
  /* ============ EDIT ME: campaign id → CTA text ============ */
  var TEXT_BY_CAMPAIGN_ID = {
    "33e957f1-c3d4-4606-8b64-c284a5be90be": "Pre Order",
  };
  var DEFAULT_PREORDER_TEXT = "Pre Order"; // for unmapped campaigns; null = skip
  /* ========================================================= */

  var variantsByProductId = {}; // productId -> array of variant id strings (or "pending")

  function getCampaigns() {
    var cfg = window.essentialPreorderConfigs;
    if (!cfg || !Array.isArray(cfg.preorderCampaigns)) return null;

    return cfg.preorderCampaigns.map(function (c) {
      var set = {};
      ((c.placement && c.placement.specificProducts) || []).forEach(function (p) {
        var num = String(p.shopifyVariantId || p.id || "").split("/").pop();
        if (num) set[num] = true;
      });
      return { id: c.id, variantIds: set };
    });
  }

  // Resolve the card's variant ids. Embedded JSON first; else one cached
  // fetch per product via the card's data-product-handle.
  function resolveVariants(card) {
    var productId = card.getAttribute("data-product-id");
    if (!productId) return null;

    if (Array.isArray(variantsByProductId[productId])) {
      return variantsByProductId[productId];
    }

    var el = card.querySelector("script[data-variants]");
    if (el) {
      try {
        var ids = JSON.parse(el.textContent).map(function (v) { return String(v.id); });
        variantsByProductId[productId] = ids;
        return ids;
      } catch (e) { /* fall through to fetch */ }
    }

    if (variantsByProductId[productId] === "pending") return null;
    var handle = card.getAttribute("data-product-handle");
    if (!handle) return null;

    variantsByProductId[productId] = "pending";
    fetch("/products/" + handle + ".js")
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        variantsByProductId[productId] = p
          ? (p.variants || []).map(function (v) { return String(v.id); })
          : [];
        schedule(); // re-apply once resolved
      })
      .catch(function () { variantsByProductId[productId] = []; });

    return null; // not resolved yet this pass
  }

  function apply() {
    var campaigns = getCampaigns();
    if (!campaigns || !campaigns.length) return;

    document.querySelectorAll("product-card.card-product").forEach(function (card) {
      var span = card.querySelector(".card-product__button span");
      if (!span) return;

      var ids = resolveVariants(card);
      if (!ids) return; // resolving async, or no product info

      var campaign = null;
      outer:
      for (var i = 0; i < campaigns.length; i++) {
        for (var j = 0; j < ids.length; j++) {
          if (campaigns[i].variantIds[ids[j]]) { campaign = campaigns[i]; break outer; }
        }
      }
      if (!campaign) return; // not preorder → keep theme text

      var text = Object.prototype.hasOwnProperty.call(TEXT_BY_CAMPAIGN_ID, campaign.id)
        ? TEXT_BY_CAMPAIGN_ID[campaign.id]
        : DEFAULT_PREORDER_TEXT;
      if (!text) return;

      // Only write when different — prevents observer feedback loops.
      if (span.textContent !== text) span.textContent = text;
    });
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; apply(); });
  }

  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  } else {
    schedule();
  }
})();